import React, { memo, useState, useEffect, useContext, ChangeEvent } from 'react'
import { useDispatch } from 'react-redux'
import { cloneDeep } from 'lodash'
import { IPricingTypes } from 'types/graphqlTypes'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { IShipperRate } from 'apps/rms/types/shipperRates'
import {
  closeEditShipperRateModal,
  updateShipperRate,
  createShipperRate,
} from 'apps/rms/utils/actions/shipperRates'
import { validateShipperRate, IValidationErrors } from 'apps/rms/utils/validators'
import { Checkbox, FormModal } from 'transfix-ui'
import {
  Shipper,
  ShipperLane,
  Date,
  Location,
  Rate,
  AwardType,
  Volume,
  EquipmentType,
  ExistingAward,
} from '../form-components'
import { DropType } from '../form-components/DropType'
import { PricingType } from '../form-components/PricingType'
import { ShipperMiles } from '../form-components/ShipperMiles'
import { ShipperRateConfirmationModal } from './ShipperRateConfirmationModal'

interface IShipperRateModalProps {
  showModal: boolean
  rate?: RecursivePartial<IShipperRate>
  errors: string
}

const defaultState: RecursivePartial<IShipperRate> = {
  pickup: {},
  delivery: {},
  isPickupDropTrailer: false,
  isDeliveryDropTrailer: false,
}

interface IValidation {
  pristine: boolean
  errors: IValidationErrors
}

const initialValidation = {
  pristine: true,
  errors: {},
} as IValidation

export const EditShipperRateModal = memo<IShipperRateModalProps>(
  ({ errors, rate: rateToEdit, showModal }) => {
    const [rate, setCurrentRate] = useState(defaultState)
    const [showConfirmationModal, setShowConfirmationModal] = useState(false)
    const [confirmationMessage, setConfirmationMessage] = useState('')
    const { getFlag } = useContext(FeatureFlagsContext)
    const { enabled: mpRmsTrueRateEnabled } = getFlag('mpRmsTrueRates')
    const showTrueRateOption = mpRmsTrueRateEnabled && !rateToEdit
    const showRepriceOption = !rateToEdit
    const [validation, setValidation] = useState<IValidation>(initialValidation)
    const shoudDisableForTrueRate = rate.pricingType === IPricingTypes.variable
    const validate = (shipperRate: RecursivePartial<IShipperRate>) => {
      const validationErrors: IValidationErrors = validateShipperRate(shipperRate, !!rateToEdit)
      setValidation({ pristine: false, errors: validationErrors })
      return !Object.keys(validationErrors).length
    }

    useEffect(() => {
      if (showModal && rateToEdit) {
        setCurrentRate(cloneDeep(rateToEdit))
      }
      if (!showModal) {
        setCurrentRate(defaultState)
      }
    }, [showModal])
    const dispatch = useDispatch()
    const shipperRateChangeHandler = (change: RecursivePartial<IShipperRate>) =>
      setCurrentRate(current => {
        const nextState = { ...current, ...change }
        if (!validation.pristine) {
          validate(nextState)
        }
        return nextState
      })

    const onConfirmationCloseHandler = () => {
      setShowConfirmationModal(false)
    }

    const closeModalHandler = () => {
      dispatch(closeEditShipperRateModal())
      setCurrentRate(defaultState)
    }

    const shouldOpenConfirmationModal = (rate: RecursivePartial<IShipperRate>): boolean => {
      if (rate.isRepricedRate && rate.volume) {
        setConfirmationMessage(
          'Volume for repriced rates is now calculated automatically.  Are you sure you want to set this volume manually?'
        )
        return true
      }
      return false
    }

    const submitForm = () => {
      if (validate(rate)) {
        if (validateShipperRate(rate, !!rateToEdit)) {
          if (shouldOpenConfirmationModal(rate)) {
            setShowConfirmationModal(true)
          } else if (rateToEdit) {
            dispatch(updateShipperRate(rate))
          } else {
            dispatch(createShipperRate(rate))
          }
        }
      }
    }

    const onRepriceCheckboxChange = ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
      setCurrentRate({
        ...rate,
        isRepricedRate: checked,
      })
    }

    return (
      <>
        <FormModal
          title={`${rateToEdit ? 'Edit' : 'Add'} Rate`}
          size={5}
          open={showModal}
          onClose={closeModalHandler}
          dataTest='modal-edit-shipper-rate'
          error={errors}
          actions={[
            {
              label: 'Cancel',
              type: 'dismiss',
              dataTest: 'cancel-shipper-rate-button',
            },
            {
              label: 'Save',
              type: 'confirm',
              action: submitForm,
              dataTest: 'save-button',
            },
          ]}>
          {showModal && (
            <>
              {showRepriceOption && (
                <div className='row'>
                  <div className='col-md-6 col-xs-6'>
                    <Checkbox label='Is Repriced Rate?' onChange={onRepriceCheckboxChange} />
                  </div>
                </div>
              )}
              <div className='row'>
                <Shipper
                  value={rate}
                  onChange={shipperRateChangeHandler}
                  error={validation.errors?.shipperApplication}
                />
                <ShipperLane value={rate} onChange={shipperRateChangeHandler} />
              </div>
              {rate.isRepricedRate && showRepriceOption && (
                <div className='row'>
                  <ExistingAward
                    value={rate}
                    onChange={shipperRateChangeHandler}
                    error={validation.errors?.existingAwardId}
                  />
                </div>
              )}
              {showTrueRateOption && (
                <div className='row'>
                  <PricingType value={rate} onChange={shipperRateChangeHandler} />
                </div>
              )}
              <div className='row'>
                <EquipmentType
                  value={rate}
                  onChange={shipperRateChangeHandler}
                  error={validation.errors?.equipment}
                />
              </div>
              <div className='row'>
                <AwardType
                  value={rate}
                  onChange={shipperRateChangeHandler}
                  error={validation.errors?.awardType}
                />
                <Volume
                  value={rate}
                  onChange={shipperRateChangeHandler}
                  volumeError={validation.errors?.volume}
                  volumeTypeError={validation.errors?.volumeType}
                />
              </div>
              <div className='row'>
                <Rate
                  value={rate}
                  onChange={shipperRateChangeHandler}
                  rateError={validation.errors.rateAmount}
                  rateTypeError={validation.errors.rateType}
                  disabled={shoudDisableForTrueRate}
                />
              </div>
              <div className='row'>
                <Location
                  value={rate}
                  type='pickup'
                  onChange={shipperRateChangeHandler}
                  error={validation.errors?.pickup}
                />
                <DropType value={rate} type='pickup' onChange={shipperRateChangeHandler} />
              </div>
              <div className='row'>
                <Location
                  value={rate}
                  type='delivery'
                  onChange={shipperRateChangeHandler}
                  error={validation.errors?.delivery}
                />
                <DropType value={rate} type='delivery' onChange={shipperRateChangeHandler} />
              </div>
              <div className='row'>
                <Date
                  value={rate}
                  onChange={shipperRateChangeHandler}
                  error={validation.errors?.startDate}
                />
              </div>
              <div className='row'>
                <ShipperMiles value={rate} onChange={shipperRateChangeHandler} />
              </div>
            </>
          )}
        </FormModal>
        <ShipperRateConfirmationModal
          onClose={onConfirmationCloseHandler}
          shipperRate={rate}
          isUpdate={!!rateToEdit}
          openConfirmation={showConfirmationModal}
          message={confirmationMessage}
        />
      </>
    )
  }
)
